import type { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleButton, ToggleButtonGroup, Paper } from '@mui/material';

import type { HeatMapRange } from '../../map-data/map-data-types';
import { theme } from '../../styles/theme';

interface Props {
  setRangeFilterType: Dispatch<SetStateAction<HeatMapRange>>;
  rangeFilterType: HeatMapRange;
}

export const HeatMapModeToggle: FC<Props> = ({
  setRangeFilterType,
  rangeFilterType,
}) => {
  const { t } = useTranslation();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: HeatMapRange | null,
  ) => {
    if (newValue !== null) {
      setRangeFilterType(newValue);
    }
  };

  return (
    <Paper
      elevation={0}
      data-tour="heatmap-mode-toggle"
      sx={{
        position: 'absolute',
        bottom: 38,
        left: 58,
        zIndex: 1000,
        backgroundColor: 'background.paper',
      }}
    >
      <ToggleButtonGroup
        value={rangeFilterType}
        exclusive
        onChange={handleChange}
        aria-label="Map data mode selection"
        size="small"
        sx={{
          backgroundColor: '#ffffff',
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: 1,
            px: 2,
            py: 1,
            fontSize: '0.75rem',
            fontWeight: 500,
            gap: 0.5,
            minWidth: 'auto',
            boxShadow: '0px 0px 1px rgba(255, 255, 255, 0.95)',
          },
          '& .MuiToggleButton-root:not(:first-of-type)': {
            borderLeft: '1px solid #e0e0e0',
          },
          '& .MuiToggleButton-root.Mui-selected[value="incidence"]': {
            backgroundColor: theme.colors['heatmap-incidence'],
            color: '#ffffff',
            borderLeft: 'none',
            '&:hover': {
              backgroundColor: theme.colors['heatmap-incidence'],
            },
          },
          '& .MuiToggleButton-root.Mui-selected[value="mortality"]': {
            backgroundColor: theme.colors['heatmap-mortality'],
            color: '#ffffff',
            borderLeft: 'none',
            '&:hover': {
              backgroundColor: theme.colors['heatmap-mortality'],
            },
          },
          '& .MuiToggleButton-root.Mui-selected[value="nccp"]': {
            backgroundColor: theme.colors['heatmap-nccp'],
            color: '#ffffff',
            borderLeft: 'none',
            '&:hover': {
              backgroundColor: theme.colors['heatmap-nccp'],
            },
          },
          '& .MuiToggleButton-root.Mui-selected[value="status"]': {
            backgroundColor: theme.colors['yellow-100'],
            color: '#ffffff',
            borderLeft: 'none',
            '&:hover': {
              backgroundColor: theme.colors['yellow-100'],
            },
          },
          '& .MuiToggleButton-root:not(.Mui-selected)': {
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          },
        }}
      >
        <ToggleButton
          value="incidence"
          aria-label="Show incidence"
          data-tour="heatmap-mode-incidence"
        >
          {t('rangeFilter.incidence')}
        </ToggleButton>
        <ToggleButton
          value="mortality"
          aria-label="Show mortality"
          data-tour="heatmap-mode-mortality"
        >
          {t('rangeFilter.mortality')}
        </ToggleButton>
        <ToggleButton
          value="nccp"
          aria-label="Show NCCP status"
          data-tour="heatmap-mode-nccp"
        >
          {t('rangeFilter.nccp')}
        </ToggleButton>
        <ToggleButton
          value="status"
          aria-label="Show country status timeline"
          data-tour="heatmap-mode-status"
        >
          {t('rangeFilter.status')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Paper>
  );
};
